import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/components/AuthProvider';
import { useEffectiveCompanyId } from '@/hooks/useEffectiveCompanyId';
import { BackgroundPattern } from '@/components/ui/BackgroundPattern';
import { CardGlow } from '@/components/ui/CardGlow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { 
  getDonationSettings, 
  updateDonationSettings, 
  getDonations,
  restoreDonationDynamicFields,
  forceCruiseShipFields
} from '@/lib/donations';
import { 
  DonationSettings as DonationSettingsType, 
  DynamicField,
  Donation 
} from '@/types/firebase';
import {
  Gift,
  PlusCircle,
  TrashIcon,
  Copy,
  Download,
  Search,
  Edit3,
  CheckCircle2,
  Sparkles,
  Compass,
  Palette,
  ClipboardList,
  MessageCircle,
} from 'lucide-react';
import { InlineEditableCell } from '@/components/ui/InlineEditableCell';
import { InlineEditableDropdown } from '@/components/ui/InlineEditableDropdown';
import { getIdToken } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { v4 as uuidv4 } from 'uuid';
import { format, isWithinInterval, startOfDay, endOfDay, isSameDay } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { getDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { CompanySettings } from '@/types/firebase';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { exportToCSV } from '@/utils/exportUtils';
import { isAchPaymentAvailable } from '@/utils/paymentGateways';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { WebformPaymentsOnboarding } from '@/components/WebformPaymentsOnboarding';

// Format currency for display
function formatCurrency(amount: number | string): string {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) {
    return '$0.00';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericAmount);
}

// Interface for react-datepicker's range
type DatePickerDateRange = [Date | null, Date | null]

export default function Donations() {
  const { user } = useAuth();
  const { effectiveCompanyId } = useEffectiveCompanyId();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyId = effectiveCompanyId || '';
  const baseUrl = window.location.origin;

  console.log('[Donations.tsx] Component render - effectiveCompanyId:', effectiveCompanyId, 'local companyId var:', companyId);
  console.log('[Donations.tsx] Full user object:', JSON.stringify({
    uid: user?.uid,
    email: user?.email,
    companyId: user?.companyId,
    effectiveCompanyId: effectiveCompanyId,
    role: user?.role,
    firstName: user?.firstName,
    lastName: user?.lastName
  }, null, 2));

  // Log when companyId changes
  useEffect(() => {
    console.log('[Donations.tsx] useEffect - effectiveCompanyId changed:', effectiveCompanyId);
  }, [effectiveCompanyId]);
  
  const [activeTab, setActiveTab] = useState<'settings' | 'donations'>(() => {
    if (typeof window === 'undefined') {
      return 'settings';
    }

    const params = new URLSearchParams(window.location.search);
    const tabFromQuery = params.get('tab');
    if (tabFromQuery === 'settings' || tabFromQuery === 'donations') {
      return tabFromQuery;
    }

    const hash = window.location.hash.replace('#', '');
    if (hash === 'settings' || hash === 'donations') {
      return hash;
    }

    return 'settings';
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState<DonationSettingsType>({
    enabled: false,
    title: '',
    subtitle: '',
    description: '',
    slug: '',
    industry: 'general',
    showPresetAmounts: true,
    presetAmounts: [10, 25, 50, 100],
    allowCustomAmount: true, // Default to true for backwards compatibility
    defaultPresetAmount: undefined,
    platformFeeEnabled: false,
    customFieldsMode: false, // Default to industry-based for backwards compatibility
    dynamicFields: [],
    thankYouRedirectUrl: '',
    thankYouMessage: 'Thank you for your payment. A confirmation email has been sent to your address.',
    acceptACH: false,
    amountLabel: 'Amount',
    headerBackgroundColor: '#4f46e5',
    headerTextColor: '#ffffff',
    // Default to collecting address for safety/back-compat
    collectAddress: true,
  });
  
  // Update date range and search states
  const [dateRange, setDateRange] = useState<DatePickerDateRange>([null, null]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<Donation | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const dynamicFieldLabelMap = useMemo(
    () => new Map((formData.dynamicFields || []).map((field) => [field.name, field.label])),
    [formData.dynamicFields]
  );
  const activeDynamicFields = formData.dynamicFields?.filter(field => !field.disabled) || [];
  const totalDonationColumns = 7 + activeDynamicFields.length;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    params.set('tab', activeTab);
    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState(null, '', newUrl);
  }, [activeTab]);

  // Fetch donation settings
  const { data: donationSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['donationSettings', companyId],
    queryFn: () => {
      if (!companyId) {
        console.error('[Donations.tsx] ERROR: Attempting to fetch donation settings without companyId!');
        return null;
      }
      return getDonationSettings(companyId);
    },
    enabled: !!companyId
  });

  // Fetch donations
  const { data: donations, isLoading: isLoadingDonations } = useQuery({
    queryKey: ['donations', companyId],
    queryFn: () => {
      console.log('[Donations.tsx] Fetching donations for companyId:', companyId);
      if (!companyId) {
        console.error('[Donations.tsx] ERROR: Attempting to fetch without companyId!');
        return [];
      }
      return getDonations(companyId);
    },
    enabled: !!companyId,
    gcTime: 0, // Don't cache at all - always fetch fresh
    staleTime: 0, // Always consider data stale
    refetchOnMount: true // Always refetch on mount
  });

  // Fetch company settings for name
  const { data: companyData } = useQuery({
    queryKey: ['company', companyId],
    queryFn: async () => {
      if (!companyId) {
        console.error('[Donations.tsx] ERROR: Attempting to fetch company data without companyId!');
        throw new Error('No company ID available');
      }
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (!companyDoc.exists()) {
        throw new Error('Company not found');
      }
      return companyDoc.data() as CompanySettings;
    },
    enabled: !!companyId
  });

  // Check if webform payments feature is enabled at company level
  const isWebformPaymentsFeatureEnabled = companyData?.features?.donations ?? false;

  const isAchGatewayEnabled = useMemo(() => isAchPaymentAvailable(companyData), [companyData]);

  const isPlatformFeeFeatureEnabled = useMemo(() => {
    const platformFeePercent = companyData?.platformFee ?? 0;
    const invoiceSettings = companyData?.invoiceSettings;
    return Boolean(invoiceSettings?.enablePlatformFee && platformFeePercent > 0);
  }, [companyData]);
  
  // Debug logging for industry fields
  useEffect(() => {
    if (donationSettings && companyData) {
      console.log('Donation settings loaded:', donationSettings);
      console.log('Company industry:', companyData?.invoiceSettings?.industry);
      console.log('Dynamic fields:', donationSettings?.dynamicFields);
    }
  }, [donationSettings, companyData]);

  useEffect(() => {
    if (!isAchGatewayEnabled && formData.acceptACH) {
      setFormData(prev => ({
        ...prev,
        acceptACH: false,
      }));
      setHasUnsavedChanges(true);
    }
  }, [formData.acceptACH, isAchGatewayEnabled]);

  useEffect(() => {
    if (!isPlatformFeeFeatureEnabled && formData.platformFeeEnabled) {
      setFormData(prev => ({
        ...prev,
        platformFeeEnabled: false,
      }));
      setHasUnsavedChanges(true);
    }
  }, [formData.platformFeeEnabled, isPlatformFeeFeatureEnabled]);

  // Update form data when settings are loaded
  useEffect(() => {
    if (donationSettings) {
      // Make sure all dynamic fields have a disabled property, defaulting to false
      const updatedFields = donationSettings.dynamicFields.map(field => ({
        ...field,
        disabled: field.disabled || false // Initialize disabled property
      }));

      setFormData({
        // Default collectAddress to true if missing
        collectAddress: donationSettings.collectAddress !== false,
        ...donationSettings,
        dynamicFields: updatedFields
      });
    }
  }, [donationSettings]);
  
  // Check for cruise ship industry if no dynamic fields are loaded
  useEffect(() => {
    if (donationSettings && companyData && 
        (!donationSettings.dynamicFields || donationSettings.dynamicFields.length === 0)) {
      
      // Check if company is likely a cruise ship from multiple sources
      const isCruiseShip = 
        companyData.invoiceSettings?.industry === 'cruise_ship' ||
        companyData.industry === 'cruise_ship' ||
        (companyData.name && 
         companyData.name.toLowerCase().includes('vie') || // Villa Vie Residences
         companyData.name.toLowerCase().includes('cruise') ||
         companyData.name.toLowerCase().includes('ship'));
      
      if (isCruiseShip) {
        console.log('Detected cruise ship company, applying fields');
        
        // Create cruise ship fields
        const cruiseFields = [
          {
            id: uuidv4(),
            name: 'cabin_number',
            label: 'Cabin Number',
            type: 'text' as const,
            required: true,
            disabled: false
          },
          {
            id: uuidv4(),
            name: 'host_name',
            label: 'Host Name',
            type: 'text' as const,
            required: false,
            disabled: false
          }
        ];
        
        // Update form data with cruise ship fields
        setFormData(prev => ({
          ...prev,
          industry: 'cruise_ship',
          dynamicFields: cruiseFields
        }));
        
        setHasUnsavedChanges(true);
        
        toast({
          title: "Cruise Ship Detected",
          description: "Applied cruise ship fields based on company settings"
        });
      }
    }
  }, [donationSettings, companyData, toast]);
  
  // Mutation for updating settings
  const updateSettingsMutation = useMutation({
    mutationFn: (settings: DonationSettingsType) => updateDonationSettings(companyId, settings),
    onSuccess: () => {
      toast({
        title: "Settings Updated",
        description: "Your payment form settings have been saved successfully"
      });
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['donationSettings', companyId] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update settings",
        variant: "destructive"
      });
    }
  });
  
  // Mutation for restoring dynamic fields
  const restoreFieldsMutation = useMutation({
    mutationFn: () => restoreDonationDynamicFields(companyId),
    onSuccess: () => {
      toast({
        title: "Dynamic Fields Restored",
        description: "Successfully restored dynamic field structure for existing submissions"
      });
      queryClient.invalidateQueries({ queryKey: ['donations', companyId] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to restore dynamic fields",
        variant: "destructive"
      });
    }
  });
  
  // Mutation for forcing cruise ship fields
  const forceCruiseShipMutation = useMutation({
    mutationFn: () => forceCruiseShipFields(companyId),
    onSuccess: (updatedSettings) => {
      toast({
        title: "Cruise Ship Fields Applied",
        description: "Successfully applied cruise ship fields including Host Name column"
      });
      setFormData(updatedSettings);
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['donationSettings', companyId] });
      queryClient.invalidateQueries({ queryKey: ['donations', companyId] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to apply cruise ship fields",
        variant: "destructive"
      });
    }
  });

  // Mutation for enabling webform payments feature
  const enableWebformPaymentsMutation = useMutation({
    mutationFn: async () => {
      if (!companyId) {
        throw new Error('No company ID available');
      }
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        'features.donations': true,
        updatedAt: serverTimestamp()
      });

      // Also enable the donation settings if not already enabled
      if (!formData.enabled) {
        const updatedSettings = {
          ...formData,
          enabled: true
        };
        await updateDonationSettings(companyId, updatedSettings);
      }
    },
    onSuccess: () => {
      toast({
        title: "Webform Payments Enabled",
        description: "You can now create and manage payment forms"
      });
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      queryClient.invalidateQueries({ queryKey: ['donationSettings', companyId] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to enable Webform Payments",
        variant: "destructive"
      });
    }
  });
  
  // Handle form changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setHasUnsavedChanges(true);
  };
  
  // Handle switch changes
  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
    setHasUnsavedChanges(true);
  };
  
  // Handle select changes
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    setHasUnsavedChanges(true);
  };
  
  // Handle preset amount changes
  const handlePresetAmountChange = (index: number, value: string) => {
    const newAmount = Number(value);
    if (isNaN(newAmount) || newAmount <= 0) return;
    
    const newAmounts = [...formData.presetAmounts];
    newAmounts[index] = newAmount;
    
    setFormData(prev => ({
      ...prev,
      presetAmounts: newAmounts
    }));
    
    setHasUnsavedChanges(true);
  };
  
  // Handle default preset amount selection
  const handleDefaultPresetAmountChange = (amount: number) => {
    setFormData(prev => ({
      ...prev,
      defaultPresetAmount: prev.defaultPresetAmount === amount ? undefined : amount
    }));
    setHasUnsavedChanges(true);
  };
  
  // Handle custom field creation
  const addCustomField = () => {
    if (formData.dynamicFields.length >= 2) return; // Max 2 fields
    
    const newField = {
      id: uuidv4(),
      name: `custom_field_${Date.now()}`,
      label: '',
      type: 'text' as const,
      required: false,
      disabled: false,
      options: [] // Initialize with empty options array
    };
    
    setFormData(prev => ({
      ...prev,
      dynamicFields: [...prev.dynamicFields, newField],
      customFieldsMode: true // Enable custom fields mode
    }));
    setHasUnsavedChanges(true);
  };
  
  // Handle custom field removal
  const removeCustomField = (fieldId: string) => {
    setFormData(prev => ({
      ...prev,
      dynamicFields: prev.dynamicFields.filter(field => field.id !== fieldId)
    }));
    setHasUnsavedChanges(true);
  };
  
  // Handle custom field updates
  const updateCustomField = (fieldId: string, updates: Partial<DynamicField>) => {
    setFormData(prev => ({
      ...prev,
      dynamicFields: prev.dynamicFields.map(field => 
        field.id === fieldId ? { ...field, ...updates } : field
      )
    }));
    setHasUnsavedChanges(true);
  };
  
  // Add new preset amount
  const addPresetAmount = () => {
    setFormData(prev => ({
      ...prev,
      presetAmounts: [...prev.presetAmounts, 0]
    }));
    setHasUnsavedChanges(true);
  };
  
  // Remove preset amount
  const removePresetAmount = (index: number) => {
    const newAmounts = [...formData.presetAmounts];
    newAmounts.splice(index, 1);
    
    setFormData(prev => ({
      ...prev,
      presetAmounts: newAmounts
    }));
    
    setHasUnsavedChanges(true);
  };
  
  // Get payment form URL
  const getDonationUrl = () => {
    // Use company name from company data
    const companyName = companyData?.name || 'yourcompany';
    // Normalize company name for URL (lowercase, remove special chars)
    const normalizedCompanyName = companyName.toLowerCase().replace(/[^a-z0-9]/g, '');
    return `${baseUrl}/${normalizedCompanyName}/${formData.slug}`;
  };
  
  // Copy form URL to clipboard
  const copyDonationUrl = () => {
    const url = getDonationUrl();
    navigator.clipboard.writeText(url);
    toast({
      title: "URL Copied",
      description: "Payment form URL copied to clipboard",
    });
  };
  
  // Save settings
  const saveSettings = () => {
    console.log('Starting saveSettings function...');
    console.log('companyId:', companyId);
    console.log('formData:', formData);
    
    try {
      // Make sure cruise ship fields are included if industry is cruise_ship
      let settingsToSave = { ...formData };
      
      // Check if we need to ensure cruise ship fields are present
      if (
        (formData.industry === 'cruise_ship' || 
         companyData?.invoiceSettings?.industry === 'cruise_ship' ||
         companyData?.industry === 'cruise_ship') &&
        (!formData.dynamicFields || formData.dynamicFields.length === 0)
      ) {
        console.log('Ensuring cruise ship fields are included in settings');
        
        // Add cruise ship fields
        const cruiseFields = [
          {
            id: uuidv4(),
            name: 'cabin_number',
            label: 'Cabin Number',
            type: 'text' as const,
            required: true,
            disabled: false
          },
          {
            id: uuidv4(),
            name: 'host_name',
            label: 'Host Name',
            type: 'text' as const,
            required: false,
            disabled: false
          }
        ];
        
        settingsToSave = {
          ...formData,
          industry: 'cruise_ship',
          dynamicFields: cruiseFields
        };
      }
      
      // Ensure all dynamic fields have required properties
      if (settingsToSave.dynamicFields && settingsToSave.dynamicFields.length > 0) {
        settingsToSave.dynamicFields = settingsToSave.dynamicFields.map(field => ({
          id: field.id || uuidv4(),
          name: field.name,
          label: field.label,
          type: field.type || 'text',
          required: typeof field.required === 'boolean' ? field.required : false,
          disabled: typeof field.disabled === 'boolean' ? field.disabled : false,
          options: field.options || []
        }));
      }
      
      // Ensure other required properties
      settingsToSave = {
        enabled: !!settingsToSave.enabled,
        title: settingsToSave.title || 'Support Our Organization',
        subtitle: settingsToSave.subtitle || '',
        description: (settingsToSave.description || '').slice(0, 70), // Ensure max 70 characters
        slug: settingsToSave.slug || 'donate',
        industry: settingsToSave.industry || 'general',
        showPresetAmounts: !!settingsToSave.showPresetAmounts,
        presetAmounts: settingsToSave.presetAmounts || [10, 25, 50, 100],
        allowCustomAmount: settingsToSave.allowCustomAmount !== undefined ? settingsToSave.allowCustomAmount : true,
        defaultPresetAmount: settingsToSave.defaultPresetAmount,
        platformFeeEnabled: !!settingsToSave.platformFeeEnabled,
        customFieldsMode: settingsToSave.customFieldsMode || false,
        dynamicFields: settingsToSave.dynamicFields || [],
        thankYouRedirectUrl: settingsToSave.thankYouRedirectUrl || '',
        thankYouMessage: settingsToSave.thankYouMessage || 'Your generous support is greatly appreciated. A thank you email has been sent to your address.',
        acceptACH: !!settingsToSave.acceptACH,
        amountLabel: settingsToSave.amountLabel || 'Amount',
        headerBackgroundColor: settingsToSave.headerBackgroundColor || '#4f46e5',
        headerTextColor: settingsToSave.headerTextColor || '#ffffff',
        // Persist collectAddress flag (default true if missing)
        collectAddress: settingsToSave.collectAddress !== false,
      };
      
      console.log('About to call mutation with settingsToSave:', Object.keys(settingsToSave));
      console.log('dynamicFields count:', settingsToSave.dynamicFields.length);
      
      if (!companyId) {
        console.error('Error: No companyId available for save operation');
        toast({
          title: "Save Failed",
          description: "Company ID is missing. Please reload the page and try again.",
          variant: "destructive"
        });
        return;
      }
      
      // Call the mutation
      updateSettingsMutation.mutate(settingsToSave);
    } catch (error) {
      console.error('Error in saveSettings function:', error);
      toast({
        title: "Save Failed",
        description: "An error occurred while preparing to save settings.",
        variant: "destructive"
      });
    }
  };
  
  // Force update industry
  const updateCompanyIndustry = async () => {
    if (!companyId) return;
    
    try {
      const companyRef = doc(db, 'companies', companyId);
      
      // Update the company industry
      await updateDoc(companyRef, {
        'industry': 'cruise_ship',
        'invoiceSettings.industry': 'cruise_ship',
        updatedAt: serverTimestamp()
      });
      
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      queryClient.invalidateQueries({ queryKey: ['donationSettings', companyId] });
      
      toast({
        title: "Industry Updated",
        description: "Company industry has been set to 'cruise_ship'"
      });
    } catch (error) {
      console.error('Error updating company industry:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update company industry",
        variant: "destructive"
      });
    }
  };
  
  // Corrected date filtering logic with explicit single-day check and logging
  const getFilteredDonations = () => {
    if (!donations) return [];
    
    const [startDate, endDate] = dateRange;

    return donations.filter(donation => {
      // Search filter (extended to include description)
      const matchesSearch = searchTerm === '' || 
        (donation.firstName && donation.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (donation.lastName && donation.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (donation.email && donation.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (typeof donation.description === 'string' && donation.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        formatCurrency(donation.amount).toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) {
        return false;
      }

      // Date filter logic
      let matchesDateRange = true;
      if (startDate) {
        const donationDate = new Date(donation.createdAt);

        if (startDate && endDate) {
          if (isSameDay(startDate, endDate)) {
            matchesDateRange = (
              donationDate.getFullYear() === startDate.getFullYear() &&
              donationDate.getMonth() === startDate.getMonth() &&
              donationDate.getDate() === startDate.getDate()
            );
          } else {
            const rangeStart = startOfDay(startDate);
            const rangeEnd = endOfDay(endDate);
            matchesDateRange = isWithinInterval(donationDate, { start: rangeStart, end: rangeEnd });
          }
        } else {
          matchesDateRange = (
            donationDate.getFullYear() === startDate.getFullYear() &&
            donationDate.getMonth() === startDate.getMonth() &&
            donationDate.getDate() === startDate.getDate()
          );
        }
      }
      
      return matchesDateRange;
    });
  };

  const handleQuickLinkNavigation = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
      event.preventDefault();

      if (activeTab !== 'settings') {
        setActiveTab('settings');
      }

      if (typeof window === 'undefined') {
        return;
      }

      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const params = new URLSearchParams(window.location.search);
        params.set('tab', 'settings');
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}#${targetId}`);
      }
    },
    [activeTab]
  );

  const openSubmissionDetails = useCallback((submission: Donation) => {
    setSelectedSubmission(submission);
    setDetailsOpen(true);
  }, []);

  const closeSubmissionDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedSubmission(null);
  }, []);

  const filteredDonations = getFilteredDonations();
  
  // Add CSV export function
  const handleExportCSV = () => {
    const enabledDynamicFields = activeDynamicFields;

    // Define base column structure
    const baseColumns = [
      { key: 'createdAt' as keyof Donation, header: 'Date' },
      { key: 'firstName' as keyof Donation, header: 'First Name' },
      { key: 'lastName' as keyof Donation, header: 'Last Name' },
      { key: 'description' as keyof Donation, header: 'Description' },
      { key: 'amount' as keyof Donation, header: 'Amount' },
      { key: 'platformFeeAmount' as keyof Donation, header: 'Platform Fee' },
      { key: 'transactionId' as keyof Donation, header: 'Transaction ID' },
    ];

    // Add dynamic field columns
    const dynamicColumns = enabledDynamicFields.map(field => ({
      // Use a unique key format, or handle flattening in formattedData
      key: `dynamic_${field.name}` as any, // Temporary key, will be mapped in formattedData
      header: field.label
    }));

    // Add status and payment method columns at the end
    const endColumns = [
      { key: 'status' as keyof Donation, header: 'Status' },
      { key: 'paymentMethod' as keyof Donation, header: 'Payment Method' },
    ];

    // Combine all columns
    const allColumns = [...baseColumns, ...dynamicColumns, ...endColumns];

    // Format data for CSV, including dynamic fields
    const formattedData = filteredDonations.map(d => {
      const baseData = {
        ...d,
        createdAt: new Date(d.createdAt).toLocaleDateString(),
        amount: formatCurrency(d.amount)
      };

      // Add dynamic field values with the temporary keys used above
      enabledDynamicFields.forEach(field => {
        (baseData as any)[`dynamic_${field.name}`] = d.dynamicFields && d.dynamicFields[field.name] 
          ? d.dynamicFields[field.name] 
          : '-';
      });

      return baseData;
    });

    // Export using utility function
    // We need to cast allColumns type because the dynamic keys are not strictly part of the original Donation type
    exportToCSV(formattedData, 'webform-payments', allColumns as { key: keyof typeof formattedData[0], header: string }[]);
  };
  
  const handleFieldUpdate = async (donationId: string, field: 'amount' | 'platformFeeAmount' | 'status', value: string | number) => {
    try {
      const token = await getIdToken(auth.currentUser!);
      const response = await fetch(`/api/donations/${donationId}/update`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ field, value }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update submission');
      }

      // Invalidate the query to refetch donations
      queryClient.invalidateQueries({ queryKey: ['donations', companyId] });

      toast({
        title: "Success",
        description: `Submission ${field} updated successfully`,
      });
    } catch (error) {
      console.error('Error updating submission:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update submission",
        variant: "destructive"
      });
      throw error;
    }
  };
  
  // Somewhere near the top of the component, add these logs
  useEffect(() => {
    console.log('Donations component mounted, companyId:', companyId);
  }, []);

  useEffect(() => {
    console.log('companyId changed:', companyId);
  }, [companyId]);

  // Show onboarding if feature is not enabled at company level OR if donation settings are disabled
  const shouldShowOnboarding = !isWebformPaymentsFeatureEnabled || !formData.enabled;

  if (shouldShowOnboarding && !isLoadingSettings) {
    return (
      <WebformPaymentsOnboarding
        onEnable={() => {
          // If feature is not enabled at company level, enable it first
          if (!isWebformPaymentsFeatureEnabled) {
            enableWebformPaymentsMutation.mutate();
          } else {
            // Otherwise just enable the donation settings
            handleSwitchChange('enabled', true);
            saveSettings();
          }
        }}
        isEnabling={enableWebformPaymentsMutation.isPending || updateSettingsMutation.isPending}
      />
    );
  }

  return (
    <div className="container py-8">
      <div className="relative">
        <BackgroundPattern />

        <div className="relative z-10">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center">
              <Gift className="h-8 w-8 text-primary mr-3" />
              <div>
                <p className="text-muted-foreground">Configure your payment form and review submissions</p>
              </div>
            </div>
            {/* Save button in header - only show on settings tab with unsaved changes */}
            {hasUnsavedChanges && activeTab === 'settings' && (
              <Button
                onClick={saveSettings}
                disabled={updateSettingsMutation.isPending}
                className="shadow-sm"
              >
                {updateSettingsMutation.isPending ? (
                  <>
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                    Saving...
                  </>
                ) : (
                  <>Save Changes</>
                )}
              </Button>
            )}
          </div>
          
          <Tabs defaultValue="settings" value={activeTab} onValueChange={(value) => setActiveTab(value as 'settings' | 'donations')} className="mt-6">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="donations">Submissions</TabsTrigger>
            </TabsList>
            
            {/* Settings Tab */}
            <TabsContent value="settings" className="mt-6">
              <div className="grid gap-6 xl:grid-cols-[320px_1fr]">
                <aside className="space-y-6">
                  <Card className="shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Sparkles className="h-5 w-5 text-primary" />
                        Design your payment journey
                      </CardTitle>
                      <CardDescription>
                        Follow the guided steps to launch a polished, on-brand payment form.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-5 text-sm">
                      <div className="rounded-lg border bg-muted p-3">
                        <div className="flex items-center justify-between text-xs font-medium uppercase text-muted-foreground">
                          <span>Status</span>
                          <Badge
                            className={cn(
                              'border-transparent px-2 py-1 text-[10px] uppercase tracking-wide',
                              formData.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'
                            )}
                          >
                            {formData.enabled ? 'Live' : 'Draft'}
                          </Badge>
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {formData.enabled
                            ? 'Your form is live and ready to accept payments. You can continue refining copy and styling without interrupting payers.'
                            : 'Complete the sections below and enable the form when you are ready to go live.'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-medium uppercase text-muted-foreground">Payment form</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 truncate rounded-md bg-muted px-2 py-1 text-xs">{getDonationUrl()}</code>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={copyDonationUrl}
                            title="Copy form URL"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => window.open(getDonationUrl(), '_blank', 'noopener,noreferrer')}
                        >
                          Preview payment form
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-medium uppercase text-muted-foreground">Launch checklist</p>
                        <ul className="space-y-2 text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" />
                            <span>Personalize your headline, description, and confirmation message.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" />
                            <span>Configure suggested payment amounts or enable flexible entry.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" />
                            <span>Collect extra payer details with up to two custom questions.</span>
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold">Quick links</CardTitle>
                      <CardDescription>Jump to the section you need.</CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-2 text-sm">
                      <a
                        href="#public-page"
                        onClick={(event) => handleQuickLinkNavigation(event, 'public-page')}
                        className="flex items-center gap-2 rounded-md border border-transparent bg-muted px-3 py-2 font-medium text-muted-foreground transition hover:border-primary hover:text-foreground"
                      >
                        <Palette className="h-4 w-4 text-primary" />
                        Overview & Branding
                      </a>
                      <a
                        href="#checkout"
                        onClick={(event) => handleQuickLinkNavigation(event, 'checkout')}
                        className="flex items-center gap-2 rounded-md border border-transparent bg-muted px-3 py-2 font-medium text-muted-foreground transition hover:border-primary hover:text-foreground"
                      >
                        <Compass className="h-4 w-4 text-primary" />
                        Payment Options
                      </a>
                      <a
                        href="#donor-experience"
                        onClick={(event) => handleQuickLinkNavigation(event, 'donor-experience')}
                        className="flex items-center gap-2 rounded-md border border-transparent bg-muted px-3 py-2 font-medium text-muted-foreground transition hover:border-primary hover:text-foreground"
                      >
                        <MessageCircle className="h-4 w-4 text-primary" />
                        Payer Experience
                      </a>
                      <a
                        href="#custom-questions"
                        onClick={(event) => handleQuickLinkNavigation(event, 'custom-questions')}
                        className="flex items-center gap-2 rounded-md border border-transparent bg-muted px-3 py-2 font-medium text-muted-foreground transition hover:border-primary hover:text-foreground"
                      >
                        <ClipboardList className="h-4 w-4 text-primary" />
                        Additional Form Fields
                      </a>
                    </CardContent>
                  </Card>
                </aside>
                <div className="space-y-6">
                  <section id="public-page" className="scroll-mt-24">
                    <Card className="overflow-hidden shadow-md">
                      <CardHeader className="bg-primary text-white">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <CardTitle>Payment Form Settings</CardTitle>
                            <CardDescription className="text-gray-100">
                              Craft the content and styling payers see before they submit.
                            </CardDescription>
                          </div>
                          <Badge className="border border-white/30 bg-white/20 text-[10px] uppercase tracking-wide text-white">
                            Overview
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-8 pt-6">
                        <div className="rounded-lg border bg-muted p-4">
                          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div className="space-y-1">
                              <Label htmlFor="enabled">Enable Payment Form</Label>
                              <p className="text-sm text-muted-foreground">
                                Turn on to allow visitors to submit payments.
                              </p>
                            </div>
                            <Switch
                              id="enabled"
                              checked={formData.enabled}
                              onCheckedChange={(checked) => handleSwitchChange('enabled', checked)}
                            />
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="title">Form Title</Label>
                              <Input
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Secure Payment Form"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="subtitle">Subtitle (Optional)</Label>
                              <Input
                                id="subtitle"
                                name="subtitle"
                                value={formData.subtitle || ''}
                                onChange={handleInputChange}
                                placeholder="Explain how this payment is used"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                              <Label htmlFor="amountLabel">Amount Label</Label>
                            <Input
                              id="amountLabel"
                              name="amountLabel"
                              value={formData.amountLabel || 'Amount'}
                              onChange={handleInputChange}
                              placeholder="Amount"
                            />
                            <p className="text-xs text-muted-foreground">
                              Customize the label for the amount section (e.g., &quot;Payment Amount&quot;, &quot;Loan Amount&quot;).
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="description">Payment Description</Label>
                            <Input
                              id="description"
                              name="description"
                              value={formData.description || ''}
                              onChange={(e) => {
                                if (e.target.value.length <= 70) {
                                  handleInputChange(e);
                                }
                              }}
                              placeholder="e.g., 'Service Deposit', 'Membership Fee' (70 chars max)"
                              maxLength={70}
                            />
                            <p className="text-xs text-muted-foreground">
                              {formData.description?.length || 0}/70 characters – Recorded with each submission for reporting.
                            </p>
                          </div>
                        </div>
                        <div className="space-y-2 rounded-lg border bg-muted p-4">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <Label htmlFor="slug">URL Slug</Label>
                            <div className="flex items-center gap-2 sm:max-w-xs">
                              <Input
                                id="slug"
                                name="slug"
                                value={formData.slug}
                                onChange={handleInputChange}
                                placeholder="pay-now"
                                className="flex-1"
                              />
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={copyDonationUrl}
                                title="Copy form URL"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Your payment form will be available at: <span className="font-mono">{getDonationUrl()}</span>
                          </p>
                        </div>
                        <div className="space-y-4 border-t pt-6">
                          <div>
                            <h4 className="text-sm font-semibold text-foreground">Form Appearance</h4>
                            <p className="text-sm text-muted-foreground">
                              Match the payment form to your brand with flexible color controls.
                            </p>
                          </div>
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="headerBackgroundColor">Header Background Color</Label>
                              <div className="flex items-center space-x-2">
                                <Input
                                  type="color"
                                  id="headerBackgroundColor"
                                  name="headerBackgroundColor"
                                  value={formData.headerBackgroundColor || '#4f46e5'}
                                  onChange={handleInputChange}
                                  className="h-10 w-12 cursor-pointer p-1"
                                />
                                <Input
                                  type="text"
                                  value={formData.headerBackgroundColor || '#4f46e5'}
                                  onChange={handleInputChange}
                                  name="headerBackgroundColor"
                                  className="flex-1"
                                  placeholder="#4f46e5"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="headerTextColor">Header Text Color</Label>
                              <div className="flex items-center space-x-2">
                                <Input
                                  type="color"
                                  id="headerTextColor"
                                  name="headerTextColor"
                                  value={formData.headerTextColor || '#ffffff'}
                                  onChange={handleInputChange}
                                  className="h-10 w-12 cursor-pointer p-1"
                                />
                                <Input
                                  type="text"
                                  value={formData.headerTextColor || '#ffffff'}
                                  onChange={handleInputChange}
                                  name="headerTextColor"
                                  className="flex-1"
                                  placeholder="#ffffff"
                                />
                              </div>
                            </div>
                          </div>
                          <div>
                            <Label className="mb-2 block text-sm">Header Preview</Label>
                            <div
                              className="relative overflow-hidden rounded-md p-4"
                              style={{
                                background: formData.headerBackgroundColor || '#4f46e5',
                                color: formData.headerTextColor || '#ffffff'
                              }}
                            >
                              <div className="relative z-10 space-y-1">
                                <h3 className="font-semibold" style={{ color: formData.headerTextColor || '#ffffff' }}
                                >
                                  {formData.title || 'Support Our Organization'}
                                </h3>
                                {formData.subtitle && (
                                  <p
                                    className="text-sm opacity-90"
                                    style={{
                                      color: formData.headerTextColor ? `${formData.headerTextColor}99` : 'rgba(255, 255, 255, 0.9)'
                                    }}
                                  >
                                    {formData.subtitle}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </section>
                  <section id="checkout" className="scroll-mt-24">
                    <Card className="overflow-hidden shadow-md">
                      <CardHeader className="bg-primary text-white">
                        <CardTitle>Payment Options</CardTitle>
                        <CardDescription className="text-gray-100">
                          Control how payers choose an amount and how payments are processed.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6 pt-6">
                        <div className="rounded-lg border bg-muted p-4">
                          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div className="space-y-1">
                              <Label htmlFor="showPresetAmounts">Show Preset Amounts</Label>
                              <p className="text-sm text-muted-foreground">
                                Display a curated list of suggested payment amounts.
                              </p>
                            </div>
                            <Switch
                              id="showPresetAmounts"
                              checked={formData.showPresetAmounts}
                              onCheckedChange={(checked) => handleSwitchChange('showPresetAmounts', checked)}
                            />
                          </div>
                        </div>
                        {formData.showPresetAmounts && (
                          <div className="space-y-4 rounded-lg border bg-muted p-4">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm font-medium">Preset Amounts</Label>
                              <Button variant="outline" size="sm" onClick={addPresetAmount}>
                                <PlusCircle className="mr-2 h-4 w-4" />
                                Add Amount
                              </Button>
                            </div>
                            <div className="space-y-2">
                              {formData.presetAmounts.map((amount, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  {!formData.allowCustomAmount && (
                                    <input
                                      type="checkbox"
                                      checked={formData.defaultPresetAmount === amount}
                                      onChange={() => handleDefaultPresetAmountChange(amount)}
                                      className="h-4 w-4 rounded border border-gray-300 text-blue-600 focus:ring-blue-500"
                                      title="Set as default amount"
                                    />
                                  )}
                                  <Input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => handlePresetAmountChange(index, e.target.value)}
                                    min="1"
                                    step="1"
                                    className="flex-1"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removePresetAmount(index)}
                                    disabled={formData.presetAmounts.length <= 1}
                                  >
                                    <TrashIcon className="h-4 w-4 text-muted-foreground" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="space-y-4 rounded-lg border bg-muted p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1">
                              <Label htmlFor="allowCustomAmount">Allow Custom Amounts</Label>
                              <p className="text-sm text-muted-foreground">
                                {formData.allowCustomAmount
                                  ? 'Payers can enter any amount they wish.'
                                  : 'Payers must choose from your preset amounts.'}
                              </p>
                              {formData.allowCustomAmount === false && !formData.defaultPresetAmount && (
                                <p className="mt-1 text-xs text-amber-600">
                                  ⚠️ Please select a default amount above when custom amounts are disabled.
                                </p>
                              )}
                            </div>
                            <Switch
                              id="allowCustomAmount"
                              checked={formData.allowCustomAmount !== false}
                              onCheckedChange={(checked) => handleSwitchChange('allowCustomAmount', checked)}
                            />
                          </div>
                          {isPlatformFeeFeatureEnabled && (
                            <div className="flex items-start justify-between gap-4 border-t pt-4">
                            <div className="space-y-1">
                              <Label htmlFor="platformFeeEnabled">Platform Fee</Label>
                              <p className="text-sm text-muted-foreground">
                                Apply your platform fee automatically to this payment form.
                              </p>
                              </div>
                              <Switch
                                id="platformFeeEnabled"
                                checked={formData.platformFeeEnabled}
                                onCheckedChange={(checked) => handleSwitchChange('platformFeeEnabled', checked)}
                              />
                            </div>
                          )}
                          {isAchGatewayEnabled && (
                            <div className="flex items-start justify-between gap-4 border-t pt-4">
                            <div className="space-y-1">
                              <Label htmlFor="acceptACH">Accept ACH Payments</Label>
                              <p className="text-sm text-muted-foreground">
                                Allow payers to pay via bank account (no platform fee applied).
                              </p>
                              </div>
                              <Switch
                                id="acceptACH"
                                checked={formData.acceptACH || false}
                                onCheckedChange={(checked) => handleSwitchChange('acceptACH', checked)}
                              />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </section>
                  <section id="donor-experience" className="scroll-mt-24">
                    <Card className="overflow-hidden shadow-md">
                      <CardHeader className="bg-primary text-white">
                        <CardTitle>Payer Experience</CardTitle>
                        <CardDescription className="text-gray-100">
                          Decide what payers see after checkout and which details you capture.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6 pt-6">
                        <div className="space-y-2">
                          <Label htmlFor="thankYouRedirectUrl">Thank You Page URL (Optional)</Label>
                          <Input
                            id="thankYouRedirectUrl"
                            name="thankYouRedirectUrl"
                            value={formData.thankYouRedirectUrl || ''}
                            onChange={handleInputChange}
                            placeholder="https://example.com/thank-you"
                          />
                          <p className="text-xs text-muted-foreground">
                            Redirect payers to a custom landing page after a successful submission.
                          </p>
                        </div>
                        {!formData.thankYouRedirectUrl && (
                          <div className="space-y-2">
                            <Label htmlFor="thankYouMessage">On-Screen Thank You Message</Label>
                            <Textarea
                              id="thankYouMessage"
                              name="thankYouMessage"
                              value={formData.thankYouMessage || ''}
                              onChange={handleInputChange}
                              placeholder="Thank you for your payment. A confirmation email has been sent to your address."
                              rows={3}
                            />
                            <p className="text-xs text-muted-foreground">
                              This message appears when you do not redirect payers to another page.
                            </p>
                          </div>
                        )}
                        <div className="rounded-lg border bg-muted p-4">
                          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div className="space-y-1">
                              <Label htmlFor="collectAddress">Collect Payer Address</Label>
                              <p className="text-sm text-muted-foreground">
                                Ask payers for street, city, state, zip, and country during checkout.
                              </p>
                            </div>
                            <Switch
                              id="collectAddress"
                              checked={formData.collectAddress !== false}
                              onCheckedChange={(checked) => handleSwitchChange('collectAddress', checked)}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </section>
                  <section id="custom-questions" className="scroll-mt-24">
                    <Card className="overflow-hidden shadow-md">
                      <CardHeader className="bg-primary text-white">
                        <CardTitle>Additional Form Fields</CardTitle>
                        <CardDescription className="text-gray-100">
                          Add up to two custom prompts to capture mission-critical payer insights.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6 pt-6">
                        {!formData.customFieldsMode && formData.dynamicFields.length > 0 && (
                          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                            <div className="mb-2 flex items-center">
                              <Gift className="mr-2 h-4 w-4 text-blue-600" />
                              <h4 className="text-sm font-medium text-blue-900">Industry Fields Active</h4>
                            </div>
                            <p className="mb-3 text-sm text-blue-700">
                              Your payment form is using industry-specific fields from your company settings.
                              These will continue to work normally.
                            </p>
                            <div className="space-y-2">
                              {formData.dynamicFields.map((field) => (
                                <div key={field.id} className="flex items-center justify-between rounded border bg-white p-2">
                                  <span className="text-sm font-medium">{field.label}</span>
                                  <div className="flex items-center space-x-3">
                                    <div className="flex items-center space-x-1">
                                      <Label className="text-xs text-gray-500">Required</Label>
                                      <Switch
                                        checked={field.required}
                                        onCheckedChange={(checked) => updateCustomField(field.id, { required: checked })}
                                      />
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <Label className="text-xs text-gray-500">Enabled</Label>
                                      <Switch
                                        checked={!field.disabled}
                                        onCheckedChange={(checked) => updateCustomField(field.id, { disabled: !checked })}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 border-t border-blue-200 pt-4">
                              <h5 className="mb-2 text-xs font-medium text-blue-900">Recovery Tools:</h5>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => restoreFieldsMutation.mutate()}
                                  disabled={restoreFieldsMutation.isPending}
                                  className="text-xs"
                                >
                                  {restoreFieldsMutation.isPending ? 'Restoring...' : 'Restore Missing Data'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="space-y-6">
                          <div className="space-y-3 rounded-lg border bg-muted p-4">
                            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                              <div className="space-y-1">
                                <Label>Additional Fields Mode</Label>
                                <p className="text-sm text-muted-foreground">
                                  Toggle to manage your own questions or use industry defaults.
                              </p>
                              </div>
                              <Switch
                                checked={formData.customFieldsMode}
                                onCheckedChange={(checked) => handleSwitchChange('customFieldsMode', checked)}
                              />
                            </div>
                            {formData.customFieldsMode && (
                              <p className="text-xs text-muted-foreground">
                                You can create up to two additional questions that appear on the payment form and in exports.
                              </p>
                            )}
                          </div>
                          {formData.customFieldsMode && formData.dynamicFields.map((field, index) => (
                            <div key={field.id} className="space-y-3 rounded-lg border bg-muted p-4">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Question {index + 1}</Label>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeCustomField(field.id)}
                                  className="h-6 w-6 text-muted-foreground hover:text-red-500"
                                >
                                  <TrashIcon className="h-3 w-3" />
                                </Button>
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <Label htmlFor={`field-${field.id}-label`} className="text-xs">
                                    Question Text
                                  </Label>
                                  <Input
                                    id={`field-${field.id}-label`}
                                    value={field.label}
                                    onChange={(e) =>
                                      updateCustomField(field.id, {
                                        label: e.target.value,
                                        name:
                                          e.target.value
                                            .toLowerCase()
                                            .replace(/[^a-z0-9]/g, '_')
                                            .substring(0, 30) || `custom_${index + 1}`
                                      })
                                    }
                                    placeholder="Enter your question (e.g., 'What is your cabin number?')"
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`field-${field.id}-type`} className="text-xs">
                                    Answer Type
                                  </Label>
                                  <Select
                                    value={field.type}
                                    onValueChange={(value: 'text' | 'number' | 'select') => {
                                      const updates: Partial<DynamicField> = { type: value };
                                      if (value === 'select' && (!field.options || field.options.length === 0)) {
                                        updates.options = [];
                                      }
                                      updateCustomField(field.id, updates);
                                    }}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="text">Text</SelectItem>
                                      <SelectItem value="number">Number</SelectItem>
                                      <SelectItem value="select">Multiple Choice</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {field.type === 'select' && (
                                  <div>
                                    <div className="mb-1 flex items-center justify-between">
                                      <Label className="text-xs">Options (one per line)</Label>
                                    </div>
                                    <Textarea
                                      value={field.options?.join('\\n') || ''}
                                      onChange={(e) =>
                                        updateCustomField(field.id, {
                                          options: e.target.value
                                            .split('\\n')
                                            .filter((option) => option.trim() !== '')
                                        })
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.stopPropagation();
                                        }
                                      }}
                                      placeholder={`Option 1\\nOption 2\\nOption 3`}
                                      rows={5}
                                      className="resize-none text-sm font-mono"
                                      style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }}
                                    />
                                    <p className="mt-1 text-xs text-muted-foreground">
                                      Enter each option on a new line. Empty lines will be ignored.
                                    </p>
                                    {field.options && field.options.some((opt) => opt.trim() !== '') && (
                                      <div className="mt-2 rounded bg-gray-50 p-2 text-xs">
                                        <strong>Preview:</strong>{' '}
                                        {field.options.filter((opt) => opt.trim() !== '').join(', ')}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="flex items-center justify-between border-t pt-2">
                                  <Label className="text-sm">Make this question required</Label>
                                  <Switch
                                    checked={field.required}
                                    onCheckedChange={(checked) => updateCustomField(field.id, { required: checked })}
                                  />
                                </div>
                                <div className="rounded-md border border-blue-200 bg-blue-50 p-2">
                                  <p className="text-xs font-medium text-blue-700">
                                    Field name for payment confirmation email:
                                  </p>
                                  <code className="mt-1 inline-block rounded border bg-white px-2 py-1 text-sm font-mono text-blue-800">
                                    {'{{' + field.name + '}}'}
                                  </code>
                                </div>
                              </div>
                            </div>
                          ))}
                          {formData.dynamicFields.length < 2 && (formData.customFieldsMode || formData.dynamicFields.length === 0) && (
                            <Button
                              variant="outline"
                              onClick={addCustomField}
                              className="flex h-20 w-full flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 text-muted-foreground hover:border-gray-400 hover:text-foreground"
                            >
                              <PlusCircle className="h-6 w-6" />
                              <span className="text-sm">Add Form Question</span>
                              <span className="text-xs">{formData.dynamicFields.length}/2 questions</span>
                            </Button>
                          )}
                          {formData.dynamicFields.length === 0 && !formData.customFieldsMode && (
                            <div className="text-center text-muted-foreground">
                              <Gift className="mx-auto mb-3 h-12 w-12 opacity-50" />
                              <h3 className="mb-1 text-sm font-medium">No Additional Questions</h3>
                              <p className="mb-4 text-xs">Add questions to collect additional information from payers.</p>
                              <Button variant="outline" onClick={addCustomField} className="text-sm">
                                <PlusCircle className="mr-1 h-4 w-4" />
                                Add Your First Question
                              </Button>
                            </div>
                          )}
                          {formData.dynamicFields.length >= 2 && (
                            <p className="py-2 text-center text-xs text-muted-foreground">
                              Maximum of 2 additional questions reached.
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </section>
                </div>
              </div>
            </TabsContent>
            
            {/* Donations Tab */}
            <TabsContent value="donations" className="mt-6">
              <CardGlow className="shadow-md">
                <Card>
                  <CardHeader>
                    <CardDescription>
                      View and manage payment submissions
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Filters and export */}
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                      {/* Search */}
                      <div className="flex-1">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            placeholder="Search submissions by name, email, or amount..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 w-full"
                          />
                        </div>
                      </div>
                      
                      {/* Date Range and Export */}
                      <div className="flex gap-2">
                        <DateRangePicker
                          dateRange={dateRange}
                          setDateRange={setDateRange}
                          align="end"
                          className="w-[240px]"
                        />
                        
                        {/* Export Button */}
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={handleExportCSV}
                          title="Export to CSV"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="rounded-md border overflow-hidden">
                      <Table>
                        <TableHeader className="bg-gray-50">
                          <TableRow>
                            <TableHead className="w-[100px]">Date</TableHead>
                            <TableHead>Payer</TableHead>
                            <TableHead>Description</TableHead>
                            {activeDynamicFields.map(field => (
                              <TableHead key={field.id} className="max-w-[45ch]">
                                {field.label}
                              </TableHead>
                            ))}
                            <TableHead className="text-right w-[110px]">Amount</TableHead>
                            <TableHead className="text-right w-[140px]">Platform Fee</TableHead>
                            <TableHead className="text-right w-[120px]">Status</TableHead>
                            <TableHead className="text-right w-[110px]">Payment Method</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {isLoadingDonations ? (
                            <TableRow>
                              <TableCell colSpan={totalDonationColumns} className="h-24 text-center">
                                Loading submissions...
                              </TableCell>
                            </TableRow>
                          ) : filteredDonations.length > 0 ? (
                            filteredDonations.map((donation) => (
                              <TableRow
                                key={donation.id}
                                onClick={() => openSubmissionDetails(donation)}
                                className="cursor-pointer transition hover:bg-muted/50"
                              >
                                <TableCell>
                                  {format(
                                    donation.createdAt instanceof Date
                                      ? donation.createdAt
                                      : new Date(donation.createdAt),
                                    'MM/dd/yyyy'
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {`${donation.firstName || ''} ${donation.lastName || ''}`.trim() || donation.email || '-'}
                                </TableCell>
                                <TableCell className="max-w-[45ch] truncate" title={donation.description || undefined}>
                                  {donation.description && donation.description.trim() !== ''
                                    ? donation.description
                                    : '-'}
                                </TableCell>
                                {activeDynamicFields.map(field => (
                                  <TableCell key={`${donation.id}-${field.id}`} className="max-w-[45ch] truncate" title={donation.dynamicFields?.[field.name] || undefined}>
                                    {donation.dynamicFields && donation.dynamicFields[field.name] ? donation.dynamicFields[field.name] : '-'}
                                  </TableCell>
                                ))}
                                <TableCell className="text-right w-[110px]" onClick={(e) => e.stopPropagation()}>
                                  {user?.role === 'super_admin' ? (
                                    <InlineEditableCell
                                      value={donation.amount}
                                      onSave={(newValue) => handleFieldUpdate(donation.id, 'amount', newValue)}
                                      type="currency"
                                      formatDisplay={(value) => formatCurrency(value as number)}
                                    />
                                  ) : (
                                    formatCurrency(donation.amount)
                                  )}
                                </TableCell>
                                <TableCell className="text-right w-[140px]" onClick={(e) => e.stopPropagation()}>
                                  {user?.role === 'super_admin' ? (
                                    donation.platformFeeAmount && donation.platformFeeAmount > 0 ? (
                                      <InlineEditableCell
                                        value={donation.platformFeeAmount}
                                        onSave={(newValue) => handleFieldUpdate(donation.id, 'platformFeeAmount', newValue)}
                                        type="currency"
                                        formatDisplay={(value) => formatCurrency(value as number)}
                                      />
                                    ) : (
                                      <InlineEditableCell
                                        value={0}
                                        onSave={(newValue) => handleFieldUpdate(donation.id, 'platformFeeAmount', newValue)}
                                        type="currency"
                                        formatDisplay={() => '-'}
                                      />
                                    )
                                  ) : (
                                    donation.platformFeeAmount && donation.platformFeeAmount > 0
                                      ? formatCurrency(donation.platformFeeAmount)
                                      : '-'
                                  )}
                                </TableCell>
                                <TableCell className="text-right w-[120px]" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex justify-end">
                                    {user?.role === 'super_admin' ? (
                                      <InlineEditableDropdown
                                        value={donation.status}
                                        options={[
                                          { value: 'pending', label: 'pending' },
                                          { value: 'completed', label: 'completed' },
                                          { value: 'failed', label: 'failed' },
                                          { value: 'refunded', label: 'refunded' },
                                          { value: 'cancelled', label: 'cancelled' },
                                        ]}
                                        onSave={(newValue) => handleFieldUpdate(donation.id, 'status', newValue)}
                                        renderValue={(value) => (
                                          <Badge className={
                                            value === 'completed'
                                              ? 'bg-green-100 text-green-800 hover:bg-green-100'
                                              : value === 'failed'
                                              ? 'bg-red-100 text-red-800 hover:bg-red-100'
                                              : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
                                          }>
                                            {value}
                                          </Badge>
                                        )}
                                      />
                                    ) : (
                                      <Badge className={
                                        donation.status === 'completed'
                                          ? 'bg-green-100 text-green-800 hover:bg-green-100'
                                          : donation.status === 'failed'
                                          ? 'bg-red-100 text-red-800 hover:bg-red-100'
                                          : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
                                      }>
                                        {donation.status}
                                      </Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right w-[110px]">
                                  {donation.paymentMethod === 'credit_card' ? 'Card' : 'Bank'}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={totalDonationColumns} className="h-24 text-center">
                                No submissions found matching your criteria.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </CardGlow>
            </TabsContent>
          </Tabs>
        </div>

        <Dialog open={detailsOpen} onOpenChange={(open) => (open ? setDetailsOpen(true) : closeSubmissionDetails())}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Payment Submission Details</DialogTitle>
              <DialogDescription>
                Snapshot of the information captured through Webform Payments.
              </DialogDescription>
            </DialogHeader>

            {selectedSubmission && (
              <div className="space-y-6">
                <div className="grid gap-4 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Payer</p>
                    <p className="font-medium">
                      {`${selectedSubmission.firstName || ''} ${selectedSubmission.lastName || ''}`.trim() || selectedSubmission.email || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Email</p>
                    <p>{selectedSubmission.email || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Amount</p>
                    <p className="font-medium">{formatCurrency(selectedSubmission.amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Status</p>
                    <p className="capitalize">{selectedSubmission.status || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Submitted</p>
                    <p>
                      {format(
                        selectedSubmission.createdAt instanceof Date
                          ? selectedSubmission.createdAt
                          : new Date(selectedSubmission.createdAt),
                        'PPpp'
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Transaction ID</p>
                    <p>{selectedSubmission.transactionId || '—'}</p>
                  </div>
                </div>

                <div className="grid gap-4 text-sm sm:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Payment Method</p>
                    <p className="font-medium">
                      {selectedSubmission.paymentMethod === 'credit_card'
                        ? `Card ending in ${selectedSubmission.cardLast4 || '----'}${selectedSubmission.cardType ? ` (${selectedSubmission.cardType})` : ''}`
                        : selectedSubmission.paymentMethod === 'ach'
                        ? `Bank account ending in ${selectedSubmission.cardLast4 || '----'}`
                        : selectedSubmission.paymentMethod || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Platform Fee</p>
                    <p>
                      {selectedSubmission.platformFeeAmount && selectedSubmission.platformFeeAmount > 0
                        ? `${formatCurrency(selectedSubmission.platformFeeAmount)}${selectedSubmission.platformFeePercentage ? ` (${selectedSubmission.platformFeePercentage}%)` : ''}`
                        : '—'}
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-xs uppercase text-muted-foreground">Description</p>
                    <p>{selectedSubmission.description || '—'}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-xs uppercase text-muted-foreground">Billing Address</p>
                    <p>
                      {[
                        selectedSubmission.address,
                        selectedSubmission.city,
                        selectedSubmission.state,
                        selectedSubmission.zip,
                      ]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </p>
                  </div>
                </div>

                {selectedSubmission.dynamicFields && Object.keys(selectedSubmission.dynamicFields).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase text-muted-foreground">Additional Form Responses</p>
                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      {Object.entries(selectedSubmission.dynamicFields).map(([key, value]) => (
                        <div key={key}>
                          <p className="font-medium">{dynamicFieldLabelMap.get(key) || key.replace(/_/g, ' ')}</p>
                          <p className="text-muted-foreground">{value || '—'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <Button variant="outline" onClick={closeSubmissionDetails}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {hasUnsavedChanges && activeTab === 'settings' && (
          <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-5 duration-300">
            <Button
              size="lg"
              onClick={saveSettings}
              disabled={updateSettingsMutation.isPending}
              className="shadow-lg"
            >
              {updateSettingsMutation.isPending ? (
                <>
                  <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                  Saving...
                </>
              ) : (
                <>Save Changes</>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
} 
